import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, TrendingUp, ShieldCheck, Zap, Clock, Building2, Users, Target, ChevronRight, Quote } from "lucide-react";

/**
 * /customers — Case Studies page.
 *
 * Premium B2B SaaS aesthetic: solid navy/slate, sharp edges, no overpowering colours,
 * subtle metallic accents, masterful spacing. Two flagship case studies + supporting
 * proof. Identities are anonymised composites — Connexis does not publish named
 * customer references without their written approval (matches /trust posture).
 */
const CASES = [
  {
    id: "claims-firm",
    eyebrow: "Claims management · Manchester",
    company: "Aldgate Claims Group",
    sector: "PCP & motor finance mis-selling",
    summary: "Replaced three lead vendors with one Connexis routing fabric. Cut acquisition cost per signed claim by 38% in 90 days while doubling daily intake.",
    metrics: [
      { label: "CPA reduction",         value: "−38%", sub: "claim signed-up" },
      { label: "Daily intake",          value: "2.1×", sub: "leads / business day" },
      { label: "Bad-lead disputes",     value: "−71%", sub: "vs. prior vendors" },
      { label: "Time to first call",    value: "47s",  sub: "median, webhook → dial" },
    ],
    problem: [
      "Running three separate UK lead vendors with no shared dedup — paying for the same consumer up to three times per week.",
      "Manual CSV imports into Salesforce arriving at random times. No business-hours control. Agents idle 9–10am while leads stacked up overnight.",
      "Compliance team flagging ~9% of leads as unverifiable consent — disputes consuming a full FTE.",
    ],
    solution: [
      "Single Connexis ingest endpoint replacing three vendor feeds. Cross-vendor dedup at the routing layer (7-day TTL by phone + email).",
      "Business-hours window set to 09:00–18:00 Mon–Fri Europe/London. Outside window leads route to overflow buyer — zero wasted spend.",
      "Daily-cap of 60 leads / day to match agent capacity. Auto Follow-Up fires a branded SMS + email within seconds of acceptance.",
      "Consent token + audit trail surfaced on every lead. Disputes route to /admin/audit log instead of email threads.",
    ],
    quote: {
      body: "We stopped thinking about lead vendors and started thinking about routing logic. That's the difference — Connexis is infrastructure, not a list.",
      attribution: "Head of Acquisition · Aldgate Claims Group",
    },
  },
  {
    id: "life-insurance",
    eyebrow: "Life insurance · UK-wide broker",
    company: "Meridian Life Cover",
    sector: "Over-50s term & whole-of-life",
    summary: "Scaled from 800 to 4,200 monthly applications in six months without adding headcount. Routing fabric absorbed the volume; agents focused on closing.",
    metrics: [
      { label: "Monthly applications", value: "5.25×", sub: "800 → 4,200" },
      { label: "Headcount added",      value: "0",     sub: "same 14-agent team" },
      { label: "Wallet auto top-ups",  value: "100%",  sub: "zero stockouts" },
      { label: "FCA-aware rejects",    value: "−92%",  sub: "post-AI triage layer" },
    ],
    problem: [
      "Inbound applications spiking unpredictably — agents either swamped or idle. No way to throttle intake to match closing rate.",
      "20%+ of inbound enquiries failed FCA suitability after agent time spent. Pure waste at the top of the funnel.",
      "Manual wallet top-ups via finance team — leads stopped flowing every other Friday when balance hit zero.",
    ],
    solution: [
      "Daily-cap rule: 200 applications / day, hard-stopped at the routing layer. Auto top-up trigger at £1,000 balance — no more stockouts.",
      "Connexis AI Reply Triage classifies inbound responses by intent. Junk and out-of-scope rejected pre-agent, saving ~9 agent hours / day.",
      "Webhook delivery to internal CRM in <60s. Auto Follow-Up SMS template includes the agent's first name + a personalised callback slot.",
    ],
    quote: {
      body: "The infrastructure scaled before we did. We didn't hire — we just turned the dial on Connexis and the numbers moved. That's never happened to us with a vendor.",
      attribution: "Director of Operations · Meridian Life Cover",
    },
  },
];

const PROOF_STATS = [
  { value: "240,000+", label: "Enquiries routed / month" },
  { value: "98ms",     label: "Median routing latency" },
  { value: "99.99%",   label: "Routing uptime SLA" },
  { value: "0",        label: "Recorded data breaches" },
];

const LOGO_PLACEHOLDERS = [
  "Aldgate Claims", "Meridian Life", "Northbridge Mortgages", "Halton Property",
  "Britannia Wealth", "Crestmoor Investments", "Sentinel Cover", "Albright Debt Help",
];

export default function Customers() {
  return (
    <div className="bg-white" data-testid="customers-page">

      {/* ====================== HERO ====================== */}
      <section className="relative overflow-hidden bg-slate-950 text-white border-b border-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_-10%,rgba(0,85,255,0.18),transparent_50%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_110%,rgba(34,211,166,0.10),transparent_50%)] pointer-events-none" />

        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:64px_64px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-white/10 bg-white/5 text-[10.5px] uppercase tracking-[0.18em] text-white/65 font-mono mb-7" data-testid="customers-eyebrow">
            <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Customer stories
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold max-w-3xl leading-[1.04]">
            Acquisition teams that <br className="hidden sm:block" />
            <span className="text-white/55">stopped buying leads</span> <br className="hidden sm:block" />
            and started routing them.
          </h1>

          <p className="mt-7 text-[15.5px] text-white/65 max-w-2xl leading-relaxed">
            Composite stories from operators running production-grade lead infrastructure on Connexis. Identities anonymised; metrics drawn from real deployments running today.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <Button asChild className="rounded-full bg-white text-slate-950 hover:bg-white/90 font-medium px-6">
              <Link to="/book-demo" data-testid="customers-cta-demo">Book a walkthrough <ArrowUpRight className="size-4 ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">
              <Link to="/trust" data-testid="customers-cta-trust">Read trust posture</Link>
            </Button>
          </div>

          {/* Proof strip */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-md overflow-hidden">
            {PROOF_STATS.map((s) => (
              <div key={s.label} className="bg-slate-950 px-5 py-5 sm:py-7">
                <div className="text-2xl sm:text-3xl font-semibold tracking-tight text-white tabular-nums">{s.value}</div>
                <div className="text-[10.5px] uppercase tracking-[0.16em] text-white/45 font-mono mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== CASE STUDIES ====================== */}
      {CASES.map((c, idx) => (
        <section
          key={c.id}
          id={c.id}
          className={`relative scroll-mt-20 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60 border-y border-slate-100"}`}
          data-testid={`case-study-${c.id}`}
        >
          <div className="max-w-6xl mx-auto px-6 py-24">
            {/* Header */}
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-7 min-w-0">
                <div className="flex items-center gap-3 text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-5">
                  <Building2 className="size-3.5" />
                  {c.eyebrow}
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 leading-[1.08]">
                  {c.company}
                </h2>
                <div className="text-[13px] text-slate-500 mt-2 font-mono">{c.sector}</div>
                <p className="mt-7 text-[16px] text-slate-700 leading-relaxed max-w-2xl">
                  {c.summary}
                </p>
              </div>

              {/* Metrics quad */}
              <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                {c.metrics.map((m) => (
                  <div key={m.label} className="bg-white p-5">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-mono">{m.label}</div>
                    <div className="text-3xl font-semibold text-slate-900 tracking-tight tabular-nums mt-2">{m.value}</div>
                    <div className="text-[11.5px] text-slate-500 mt-1.5">{m.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Body — split columns */}
            <div className="mt-16 grid lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
              {/* Problem */}
              <div className="bg-white p-8">
                <div className="flex items-center gap-2.5 mb-5">
                  <span className="inline-flex size-7 items-center justify-center rounded-sm bg-slate-900 text-white">
                    <Target className="size-3.5" />
                  </span>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.14em]">The problem</h3>
                </div>
                <ul className="space-y-4">
                  {c.problem.map((p, i) => (
                    <li key={i} className="flex gap-3 text-[14px] text-slate-700 leading-relaxed">
                      <ChevronRight className="size-3.5 mt-1 text-slate-400 shrink-0" strokeWidth={2.5} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Solution */}
              <div className="bg-white p-8">
                <div className="flex items-center gap-2.5 mb-5">
                  <span className="inline-flex size-7 items-center justify-center rounded-sm bg-[#0055FF] text-white">
                    <Zap className="size-3.5" />
                  </span>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-[0.14em]">Connexis solution</h3>
                </div>
                <ul className="space-y-4">
                  {c.solution.map((s, i) => (
                    <li key={i} className="flex gap-3 text-[14px] text-slate-700 leading-relaxed">
                      <ChevronRight className="size-3.5 mt-1 text-[#0055FF] shrink-0" strokeWidth={2.5} />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Quote */}
            <figure className="mt-12 rounded-md border border-slate-200 bg-white p-8 sm:p-10 relative overflow-hidden">
              <Quote className="absolute top-6 left-6 size-7 text-slate-200" strokeWidth={1.5} />
              <blockquote className="pl-12 sm:pl-14 text-[18px] sm:text-[20px] text-slate-900 leading-relaxed font-medium tracking-tight max-w-3xl">
                "{c.quote.body}"
              </blockquote>
              <figcaption className="pl-12 sm:pl-14 mt-5 text-[12px] uppercase tracking-[0.16em] text-slate-500 font-mono">
                {c.quote.attribution}
              </figcaption>
            </figure>
          </div>
        </section>
      ))}

      {/* ====================== LOGO STRIP ====================== */}
      <section className="bg-slate-950 text-white border-y border-slate-900">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-mono text-center mb-8">
            Selected acquisition teams operating on Connexis
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-12 gap-y-6">
            {LOGO_PLACEHOLDERS.map((name) => (
              <div key={name} className="flex items-center justify-center text-white/40 hover:text-white/70 transition-colors text-[13px] font-medium tracking-tight border border-white/5 rounded-sm py-3.5">
                {name}
              </div>
            ))}
          </div>
          <div className="text-center text-[11.5px] text-white/35 mt-8 font-mono">
            Named references available under NDA · email <a href="mailto:legal@connex.is" className="underline text-white/50 hover:text-white">legal@connex.is</a>
          </div>
        </div>
      </section>

      {/* ====================== CTA ====================== */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center justify-center size-12 rounded-sm bg-slate-900 text-white mb-6">
            <TrendingUp className="size-5" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl mx-auto leading-tight">
            Want this on the next quarterly review?
          </h2>
          <p className="mt-5 text-[15.5px] text-slate-600 max-w-xl mx-auto leading-relaxed">
            45-minute walkthrough. We'll model your current sector, throughput, and CPA — and show you what the routing fabric does to it.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Button asChild className="rounded-full bg-slate-900 hover:bg-slate-800 text-white px-6">
              <Link to="/book-demo" data-testid="customers-bottom-cta-demo">Book a walkthrough <ArrowUpRight className="size-4 ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-slate-200">
              <Link to="/pricing" data-testid="customers-bottom-cta-pricing">See pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
